import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { openUpgradeSheet } from './UpgradeSheet';
import { Info, Zap, CreditCard, ArrowRight } from 'lucide-react';

/**
 * Plan-aware nudge banner for Free/Trial orgs on the Bills / upload screens.
 * Reacts to the one-bill-at-a-time limit and the remaining bill-credit state.
 * Renders nothing for paid plans or when there is nothing to nudge about.
 */
export default function PlanNudgeBanner({ accountId }) {
  const [b, setB] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!accountId) return;
    const fetchState = async () => {
      try {
        const { data } = await api.get('/subscription/plan-banner', { params: { account_id: accountId } });
        if (alive) setB(data);
      } catch { /* silent */ }
    };
    fetchState();
    const t = setInterval(fetchState, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [accountId]);

  if (!b || !b.is_free_or_trial) return null;
  if (!b.one_at_a_time && !b.out_of_credits) return null;

  const outOfCredits = b.out_of_credits;
  const Icon = outOfCredits ? CreditCard : Zap;
  const tone = outOfCredits
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : 'border-sky-300 bg-sky-50 text-sky-900';
  const title = outOfCredits ? 'Out of bill credits' : 'One bill at a time';
  const msg = outOfCredits
    ? `Your ${b.plan_label || 'Free'} plan is out of bill credits. Buy credits (from $${b.pay_per_bill_usd}/bill) or upgrade to keep processing.`
    : `Your ${b.plan_label || 'Free'} plan processes one bill at a time — ${b.in_flight} bill is processing now. Upgrade to run bills in parallel.`;

  return (
    <div className={`flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${tone}`} data-testid="plan-nudge-banner">
      <div className="flex items-center gap-3 min-w-0">
        <Icon className="h-5 w-5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            {title}
            <Info className="h-3.5 w-3.5 opacity-60" />
          </div>
          <div className="text-sm opacity-90 truncate">{msg}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => openUpgradeSheet(outOfCredits ? 'credits' : 'plans', accountId)}
        data-testid="plan-nudge-upgrade"
        className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-[hsl(221,100%,33%)] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[hsl(221,100%,27%)] transition-colors"
      >
        {outOfCredits ? 'Buy credits / Upgrade' : 'Upgrade'} <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
